import _ from 'lodash';

import { ServiceClient } from '@services/coralservices/ServiceClient';
import IhmLaborTrackingService from 'IhmLaborTrackingService-coral-javascript-clients/store-specific-jquery-client.js';

const client = new ServiceClient('IhmLaborTrackingService', IhmLaborTrackingService, true);

export interface IGetIndirectTasksOutput {
    IndirectTasksList: { [zone: string]: string[] };
}

export interface IGetMasterIndirectTasksOutput {
    MasterIndirectTasksList: {
        idlePromptDisabled: boolean;
        tasks: {
            [zone: string]: {
                directTasks?: string[];
                idlePromptThreshold?: number;
                timeOffThreshold?: number;
            };
        };
    };
}

export interface IAssociateAuditDataItem {
    requestId: string;
    process: string;
    storeId: string;
    storeFamily: string;
    timestamp: number;
    timezone: string;
    username: string;
    zone: string;
    subzone: string;
    notes: string;
}

export interface IGetAssociateAuditDataOutput {
    AssociateAuditData: IAssociateAuditDataItem[];
}

export class IhmLaborTrackingServiceClient {
    /**
     * Expects a serialized JSON payload (not an object, but a string)
     *
     * @param storeId {string} - storeId the request is originating in (this is not needed for the coral API
     *                                 call but instead is used for Coral API routing as storeIds are used in store
     *                                 specific URL routing)
     * @param storeFamily {string} - storeFamily the request is for
     */
    public static getIndirectTasks(storeId: string, storeFamily: string) {
        return this.sendRequest<IGetIndirectTasksOutput>(
            'GetIndirectTasks',
            {
                storeId,
                storeFamily,
            },
            storeId,
        );
    }

    /**
     * Expects a serialized JSON payload (not an object, but a string)
     *
     * @param storeId {string} - storeId the request is originating in (this is not needed for the coral API
     *                                 call but instead is used for Coral API routing as storeIds are used in store
     *                                 specific URL routing)
     * @param storeFamily {string} - storeFamily the request is for
     */
    public static getMasterIndirectTasks(storeId: string, storeFamily: string) {
        return this.sendRequest<IGetMasterIndirectTasksOutput>(
            'GetMasterIndirectTasks',
            {
                storeId,
                storeFamily,
            },
            storeId,
        );
    }

    /**
     * Expects a serialized JSON payload (not an object, but a string)
     *
     * @param storeId {string} - storeId the request is for
     * @param payload {string} - the serialized payload (assumed to be valid)
     */
    public static recordAssociateAudit(storeId: string, payload: string) {
        return this.sendRequest<void>(
            'RecordAssociateAudit',
            {
                storeId,
                data: payload,
            },
            storeId,
        );
    }

    /**
     * Expects a serialized JSON payload (not an object, but a string)
     *
     * @param storeId {string} - storeId the request is for
     * @param durationMs {integer} - duration in milliseconds
     * @param payload {string} - the serialized payload (assumed to be valid)
     */
    public static recordAssociateDuration(storeId: string, durationMs: number, payload: string) {
        return this.sendRequest<void>(
            'RecordAssociateDuration',
            {
                storeId,
                durationMs,
                data: payload,
            },
            storeId,
        );
    }

    /**
     * GetAssociateAuditData
     *
     * @param storeId {string} - storeId the request is for
     * @param fromTimestamp {integer} - start timestamp of the request range (ms)
     * @param toTimestamp {integer} - end timestamp of the request range (ms)
     * @param processes {com.amazon.ihmlabortrackingservice#ProcessList} - list of event processes
     * @return {com.amazon.ihmlabortrackingservice#GetAssociateAuditDataOutput}
     */
    public static getAssociateAuditData(storeId: string, fromTimestamp: number, toTimestamp: number, processes: string[]) {
        return this.sendRequest<IGetAssociateAuditDataOutput>(
            'GetAssociateAuditData',
            {
                storeId,
                fromTimestamp,
                toTimestamp,
                processes,
            },
            storeId,
        );
    }

    private static sendRequest<T>(operation: string, data: { [key: string]: any }, storeId: string): Promise<T> {
        const dataToSend: { [key: string]: any } = _.cloneDeep(data);
        dataToSend.storeId = storeId; // Include the storeId in the input for store isolation routing
        return client.sendRequest(
            {
                operation,
                data: dataToSend,
            },
            storeId,
        );
    }
}
